import React, { useState, useEffect } from 'react';
import axios from 'axios';

const AdminPortal = () => {
    const [dashboardData, setDashboardData] = useState({
        total_clients: 0,
        total_ads: 0,
        total_auctions: 0,
        active_contracts: 0
    });
    const [complianceIssues, setComplianceIssues] = useState([]);
    const [pendingDesignRequests, setPendingDesignRequests] = useState([]);
    const [recentActivity, setRecentActivity] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('dashboard');
    const [clients, setClients] = useState([]);
    const [ads, setAds] = useState([]);
    const [stripeData, setStripeData] = useState(null);
    const [databaseInfo, setDatabaseInfo] = useState(null);
    const [showClients, setShowClients] = useState(false);
    const [showAds, setShowAds] = useState(false);
    const [showStripeData, setShowStripeData] = useState(false);
    const [showDatabaseInfo, setShowDatabaseInfo] = useState(false);

    useEffect(() => {
        fetchDashboardData();
        fetchComplianceIssues();
        fetchPendingDesignRequests();
        fetchRecentActivity();
    }, []);

    const fetchDashboardData = async () => {
        try {
            const response = await axios.get('/api/admin/dashboard');
            setDashboardData(response.data);
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
        }
    };

    const fetchComplianceIssues = async () => {
        try {
            const response = await axios.get('/api/admin/compliance-issues');
            setComplianceIssues(response.data);
        } catch (error) {
            console.error('Error fetching compliance issues:', error);
        }
    };

    const fetchPendingDesignRequests = async () => {
        try {
            const response = await axios.get('/api/admin/design-requests');
            setPendingDesignRequests(response.data);
        } catch (error) {
            console.error('Error fetching design requests:', error);
        }
    };

    const fetchRecentActivity = async () => {
        try {
            const response = await axios.get('/api/admin/recent-activity');
            setRecentActivity(response.data);
        } catch (error) {
            console.error('Error fetching recent activity:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleBackupDatabase = async () => {
        try {
            const response = await axios.post('/api/admin/backup');
            alert(`Database backup initiated successfully! Backup ID: ${response.data.backup_id}`);
        } catch (error) {
            console.error('Error initiating backup:', error);
            alert('Failed to initiate backup: ' + (error.response?.data?.error || error.message));
        }
    };

    const handleSendBulkEmail = async (emailType) => {
        try {
            const response = await axios.post('/api/admin/bulk-email', {
                email_type: emailType
            });
            alert(`${emailType} emails sent successfully! Sent to ${response.data.sent_count} recipients.`);
        } catch (error) {
            console.error('Error sending bulk emails:', error);
            alert('Failed to send bulk emails: ' + (error.response?.data?.error || error.message));
        }
    };

    const handleResolveComplianceIssue = async (issueId) => {
        try {
            await axios.post(`/api/admin/compliance-issues/${issueId}/resolve`);
            fetchComplianceIssues(); // Refresh the list
            alert('Compliance issue marked as resolved');
        } catch (error) {
            console.error('Error resolving compliance issue:', error);
            alert('Failed to resolve compliance issue: ' + (error.response?.data?.error || error.message));
        }
    };

    const handleViewAllClients = async () => {
        try {
            const response = await axios.get('/api/admin/clients');
            setClients(response.data);
            setShowClients(true);
            setShowAds(false);
            setShowStripeData(false);
            setShowDatabaseInfo(false);
        } catch (error) {
            console.error('Error fetching clients:', error);
            alert('Failed to fetch clients: ' + (error.response?.data?.error || error.message));
        }
    };

    const handleViewAllAds = async () => {
        try {
            const response = await axios.get('/api/admin/ads');
            setAds(response.data);
            setShowAds(true);
            setShowClients(false);
            setShowStripeData(false);
            setShowDatabaseInfo(false);
        } catch (error) {
            console.error('Error fetching ads:', error);
            alert('Failed to fetch ads: ' + (error.response?.data?.error || error.message));
        }
    };

    const handleViewStripeData = async () => {
        try {
            const response = await axios.get('/api/admin/stripe-data');
            setStripeData(response.data);
            setShowStripeData(true);
            setShowClients(false);
            setShowAds(false);
            setShowDatabaseInfo(false);
        } catch (error) {
            console.error('Error fetching Stripe data:', error);
            alert('Failed to fetch Stripe data: ' + (error.response?.data?.error || error.message));
        }
    };

    const handleViewDatabaseInfo = async () => {
        try {
            const response = await axios.get('/api/admin/database-info');
            setDatabaseInfo(response.data);
            setShowDatabaseInfo(true);
            setShowClients(false);
            setShowAds(false);
            setShowStripeData(false);
        } catch (error) {
            console.error('Error fetching database info:', error);
            alert('Failed to fetch database info: ' + (error.response?.data?.error || error.message));
        }
    };

    if (loading) {
        return <div className="admin-portal loading">Loading admin portal...</div>;
    }

    return (
        <div className="admin-portal">
            <div className="admin-header">
                <h1>Admin Portal</h1>
                <div className="admin-actions">
                    <button onClick={handleBackupDatabase} className="btn btn-primary">
                        Backup Database
                    </button>
                    <button onClick={() => handleSendBulkEmail('clients')} className="btn btn-secondary">
                        Email All Clients
                    </button>
                    <button onClick={() => handleSendBulkEmail('hosts')} className="btn btn-secondary">
                        Email All Hosts
                    </button>
                </div>
            </div>

            <div className="admin-tabs">
                <button 
                    className={`tab ${activeTab === 'dashboard' ? 'active' : ''}`}
                    onClick={() => setActiveTab('dashboard')}
                >
                    Dashboard
                </button>
                <button 
                    className={`tab ${activeTab === 'compliance' ? 'active' : ''}`}
                    onClick={() => setActiveTab('compliance')}
                >
                    Legal Compliance
                </button>
                <button 
                    className={`tab ${activeTab === 'design' ? 'active' : ''}`}
                    onClick={() => setActiveTab('design')}
                >
                    Design Requests
                </button>
                <button 
                    className={`tab ${activeTab === 'activity' ? 'active' : ''}`}
                    onClick={() => setActiveTab('activity')}
                >
                    Recent Activity
                </button>
            </div>

            <div className="admin-content">
                {activeTab === 'dashboard' && (
                    <div className="dashboard">
                        <div className="stats-grid">
                            <div className="stat-card">
                                <h3>Total Clients</h3>
                                <p className="stat-number">{dashboardData.total_clients}</p>
                            </div>
                            <div className="stat-card">
                                <h3>Total Ads</h3>
                                <p className="stat-number">{dashboardData.total_ads}</p>
                            </div>
                            <div className="stat-card">
                                <h3>Active Auctions</h3>
                                <p className="stat-number">{dashboardData.total_auctions}</p>
                            </div>
                            <div className="stat-card">
                                <h3>Active Contracts</h3>
                                <p className="stat-number">{dashboardData.active_contracts}</p>
                            </div>
                        </div>

                        <div className="quick-actions">
                            <h3>Quick Actions</h3>
                            <div className="action-buttons">
                                <button onClick={handleViewAllClients} className="btn btn-success">View All Clients</button>
                                <button onClick={handleViewAllAds} className="btn btn-info">View All Ads</button>
                                <button onClick={handleViewStripeData} className="btn btn-warning">View Stripe Data</button>
                                <button onClick={handleViewDatabaseInfo} className="btn btn-danger">View Database Info</button>
                            </div>
                        </div>

                        {/* Quick Action Results */}
                        {showClients && (
                            <div className="quick-action-results">
                                <h3>All Clients</h3>
                                <button onClick={() => setShowClients(false)} className="btn btn-sm btn-secondary">Close</button>
                                <div className="clients-list">
                                    {clients.length === 0 ? (
                                        <p>No clients found.</p>
                                    ) : (
                                        clients.map((client, index) => (
                                            <div key={index} className="client-item">
                                                <h4>{client.name}</h4>
                                                <p>Email: {client.email}</p>
                                                <p>Company: {client.company_name || 'N/A'}</p>
                                                <p>Created: {new Date(client.created_at).toLocaleDateString()}</p>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        )}

                        {showAds && (
                            <div className="quick-action-results">
                                <h3>All Ads</h3>
                                <button onClick={() => setShowAds(false)} className="btn btn-sm btn-secondary">Close</button>
                                <div className="ads-list">
                                    {ads.length === 0 ? (
                                        <p>No ads found.</p>
                                    ) : (
                                        ads.map((ad, index) => (
                                            <div key={index} className="ad-item">
                                                <h4>{ad.title}</h4>
                                                <p>Client: {ad.client_name}</p>
                                                <p>Status: {ad.status}</p>
                                                <p>Created: {new Date(ad.created_at).toLocaleDateString()}</p>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        )}

                        {showStripeData && (
                            <div className="quick-action-results">
                                <h3>Stripe Data</h3>
                                <button onClick={() => setShowStripeData(false)} className="btn btn-sm btn-secondary">Close</button>
                                <div className="stripe-data">
                                    {stripeData ? (
                                        <pre>{JSON.stringify(stripeData, null, 2)}</pre>
                                    ) : (
                                        <p>Loading Stripe data...</p>
                                    )}
                                </div>
                            </div>
                        )}

                        {showDatabaseInfo && (
                            <div className="quick-action-results">
                                <h3>Database Information</h3>
                                <button onClick={() => setShowDatabaseInfo(false)} className="btn btn-sm btn-secondary">Close</button>
                                <div className="database-info">
                                    {databaseInfo ? (
                                        <pre>{JSON.stringify(databaseInfo, null, 2)}</pre>
                                    ) : (
                                        <p>Loading database info...</p>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'compliance' && (
                    <div className="compliance">
                        <h3>Legal Compliance Issues</h3>
                        {complianceIssues.length === 0 ? (
                            <p>No compliance issues found.</p>
                        ) : (
                            <div className="compliance-issues">
                                {complianceIssues.map((issue, index) => (
                                    <div key={index} className={`compliance-issue ${issue.severity}`}>
                                        <div className="issue-header">
                                            <h4>{issue.state} - {issue.issue_type}</h4>
                                            <span className={`severity ${issue.severity}`}>
                                                {issue.severity.toUpperCase()}
                                            </span>
                                        </div>
                                        <p>{issue.description}</p>
                                        <div className="issue-actions">
                                            <button 
                                                onClick={() => handleResolveComplianceIssue(issue.id)}
                                                className="btn btn-sm btn-success"
                                            >
                                                Mark Resolved
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'design' && (
                    <div className="design-requests">
                        <h3>Pending Design Requests</h3>
                        {pendingDesignRequests.length === 0 ? (
                            <p>No pending design requests.</p>
                        ) : (
                            <div className="design-requests-list">
                                {pendingDesignRequests.map((request, index) => (
                                    <div key={index} className="design-request">
                                        <div className="request-header">
                                            <h4>{request.client_name}</h4>
                                            <span className="fee">${request.service_fee}</span>
                                        </div>
                                        <p><strong>Ad Title:</strong> {request.ad_title}</p>
                                        <p><strong>Creative Brief:</strong> {request.creative_brief}</p>
                                        <div className="request-actions">
                                            <button className="btn btn-sm btn-primary">Contact Client</button>
                                            <button className="btn btn-sm btn-success">Mark Complete</button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'activity' && (
                    <div className="recent-activity">
                        <h3>Recent Activity</h3>
                        {recentActivity.length === 0 ? (
                            <p>No recent activity.</p>
                        ) : (
                            <div className="activity-list">
                                {recentActivity.map((activity, index) => (
                                    <div key={index} className="activity-item">
                                        <span className="activity-time">{activity.timestamp}</span>
                                        <span className="activity-type">{activity.type}</span>
                                        <span className="activity-description">{activity.description}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminPortal;